// CampaignDrawer.js - Two tabs with country-based crop filtering and language filtering
import React, { useState, useEffect, useCallback } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import { Icon } from '@iconify/react/dist/iconify.js';
import { addCampaign, updateCampaign, getCropShort, getLanguages, getCountries, getPages } from '../lib/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    column-gap: 1rem !important;
    gap: 0.25rem !important;
    width: 100% !important;
    margin-bottom: 0.25rem !important;
  }
  .form-grid-container > div {
    width: 100% !important;
  }
  .form-control, select, textarea {
    width: 100% !important;
    box-sizing: border-box !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
    font-size: 0.875rem !important;
  }
  .form-control:focus, select:focus, textarea:focus {
    border-color: #1a73e8 !important;
    box-shadow: 0 0 0 0.2rem rgba(26, 115, 232, 0.25) !important;
    outline: none !important;
  }
  .error-message {
    color: #dc3545;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
  .card-body {
    overflow-y: auto !important;
    max-height: calc(100vh - 80px) !important;
    padding: 1.5rem !important;
  }
  .drawer-header {
    padding: 1.5rem;
    border-bottom: 1px solid #dee2e6;
    position: relative;
  }
  .drawer-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a73e8;
  }
  .form-label {
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    color: #4a4a4a !important;
    margin-bottom: 0.5rem !important;
    display: block;
  }
  .required-star {
    color: #dc3545;
  }
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }
  .section-title {
    font-size: 1rem;
    font-weight: 600;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e2e8f0;
  }
  .tab-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
  }
  .style-check .form-check-input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  .style-check .form-check-input:checked {
    background-color: #1a73e8;
    border-color: #1a73e8;
  }
  .main-tab {
    padding: 8px 20px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.875rem;
    transition: all 0.2s;
  }
  .main-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }
  .main-tab:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Checklist Styles */
  .checklist-container {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    background: #f8fafc;
    margin-bottom: 1.25rem;
  }

  .checklist-title {
    font-weight: 600;
    margin-bottom: 12px;
    color: #374151;
    font-size: 0.875rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .checklist-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
    max-height: 200px;
    overflow-y: auto;
    padding: 8px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }

  .checklist-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 6px;
    transition: background-color 0.2s;
    cursor: pointer;
  }

  .checklist-item:hover {
    background: #f3f4f6;
  }

  .checklist-checkbox {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 2px solid #d1d5db;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
  }

  .checklist-checkbox.checked {
    background: #1a73e8;
    border-color: #1a73e8;
  }

  .checklist-checkbox.checked::after {
    content: '✓';
    color: white;
    font-size: 12px;
    font-weight: bold;
  }

  .checklist-label {
    cursor: pointer;
    font-size: 0.875rem;
    color: #374151;
  }

  .selected-count {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 8px;
  }

  .select-all-btn {
    padding: 4px 8px;
    font-size: 0.75rem;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .select-all-btn:hover {
    background: #e5e7eb;
  }

  .no-crops-message {
    text-align: center;
    padding: 20px;
    color: #6b7280;
    font-size: 0.875rem;
  }

  /* Banner Styles */
  .banner-upload-section {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    background: #f8fafc;
  }

  .banner-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem;
    background: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
  }

  .banner-item:hover {
    background: #f9fafb;
  }

  .file-input-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 150px;
  }

  .file-input-label {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 12px;
    background: #f8f9fa;
    border: 1px dashed #ced4da;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    gap: 4px;
  }

  .file-input-label:hover {
    background: #e9ecef;
    border-color: #1a73e8;
  }

  .file-input {
    position: absolute;
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    z-index: -1;
  }

  .upload-progress {
    font-size: 0.75rem;
    color: #1a73e8;
    margin-top: 5px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .banner-language-select {
    min-width: 150px;
    flex: 1;
  }

  .banner-display-order {
    width: 80px;
  }

  .remove-banner-btn {
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
  }

  .remove-banner-btn:hover {
    background: #c82333;
  }

  .add-banner-btn {
    background: #28a745;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .add-banner-btn:hover {
    background: #218838;
  }

  .status-section {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
  }

  .section-divider {
    margin: 0.5rem 0;
    border-top: 2px solid #e2e8f0;
  }

  .country-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 4px;
    font-style: italic;
  }

  .no-languages-message {
    text-align: center;
    padding: 20px;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .banner-existing-file {
    font-size: 0.875rem;
    color: #28a745;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: #f0fdf4;
    border-radius: 4px;
    border: 1px solid #bbf7d0;
  }
`;

const CampaignDrawer = ({ isOpen, onClose, campaignToEdit, onSuccess }) => {
  const [saveLoading, setSaveLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Data states
  const [cropsList, setCropsList] = useState([]);
  const [languagesList, setLanguagesList] = useState([]);
  const [countriesList, setCountriesList] = useState([]);
  const [pagesList, setPagesList] = useState([]);

  // Selected IDs states
  const [selectedCropIds, setSelectedCropIds] = useState([]);
  const [selectedCountryIds, setSelectedCountryIds] = useState([]);
  const [selectedPageIds, setSelectedPageIds] = useState([]);

  // Search states
  const [cropSearch, setCropSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [pageSearch, setPageSearch] = useState('');

  // Banner management
  const [banners, setBanners] = useState([]);
  const [bannerFiles, setBannerFiles] = useState({});
  const [bannersToRemove, setBannersToRemove] = useState([]);

  // Loading states
  const [loadingCrops, setLoadingCrops] = useState(false);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);

  // Track loaded data
  const [dataLoaded, setDataLoaded] = useState({
    crops: false,
    languages: false,
    countries: false,
    pages: false,
  });

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      campaign: {
        Name: '',
        Description: '',
        StartDate: '',
        EndDate: '',
        Priority: 0,
        IsActive: true,
      },
    },
  });

  const getAuthToken = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error('Authentication token not found. Please login again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return null;
    }
    return token;
  };

  // Parse AllowedCountries from JSON string
  const parseAllowedCountries = (allowedCountries) => {
    if (!allowedCountries) return [];
    try {
      return JSON.parse(allowedCountries);
    } catch (e) {
      return [];
    }
  };

  // Filter crops based on selected countries
  const getFilteredCrops = () => {
    if (selectedCountryIds.length === 0) {
      return getFilteredItems(cropsList, cropSearch, ['CropName', 'CropShortName']);
    }

    const filtered = cropsList.filter(crop => {
      const allowedCountries = parseAllowedCountries(crop.AllowedCountries);
      return allowedCountries.some(countryId => selectedCountryIds.includes(countryId));
    });

    return getFilteredItems(filtered, cropSearch, ['CropName', 'CropShortName']);
  };

  const ENGLISH_LANGUAGE_ID = "701258p3-a02f-11f0-af44-56515c885827";

  const isEnglishLanguage = (languageId) => languageId === ENGLISH_LANGUAGE_ID;

  const getFilteredLanguages = useCallback(() => {
    const countryLanguages = selectedCountryIds.length === 0
      ? []
      : languagesList.filter(lang => selectedCountryIds.includes(lang.CountryID));

    const hasEnglish = countryLanguages.some(lang => isEnglishLanguage(lang.ID));
    if (hasEnglish) {
      return countryLanguages;
    }

    const englishLang = languagesList.find(lang => isEnglishLanguage(lang.ID));
    return englishLang ? [englishLang, ...countryLanguages] : countryLanguages;
  }, [languagesList, selectedCountryIds]);

  // Load all data when drawer opens
  useEffect(() => {
    if (!isOpen) return;

    const loadAllData = async () => {
      const token = getAuthToken();
      if (!token) return;

      // Load Crops using getCropShort
      if (!dataLoaded.crops) {
        setLoadingCrops(true);
        try {
          const cropsRes = await getCropShort(token);
          let cropsData = [];
          if (cropsRes.data && Array.isArray(cropsRes.data)) {
            cropsData = cropsRes.data;
          } else if (Array.isArray(cropsRes)) {
            cropsData = cropsRes;
          }
          setCropsList(cropsData);
          setDataLoaded(prev => ({ ...prev, crops: true }));
        } catch (error) {
          console.error("Failed to load crops:", error);
          toast.error('Failed to load crops data', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } finally {
          setLoadingCrops(false);
        }
      }

      // Load Countries
      if (!dataLoaded.countries) {
        setLoadingCountries(true);
        try {
          const countriesRes = await getCountries(token);
          setCountriesList(countriesRes.countries || countriesRes.data || []);
          setDataLoaded(prev => ({ ...prev, countries: true }));
        } catch (error) {
          console.error("Failed to load countries:", error);
          toast.error('Failed to load countries data', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } finally {
          setLoadingCountries(false);
        }
      }

      // Load Pages
      if (!dataLoaded.pages) {
        setLoadingPages(true);
        try {
          const pagesRes = await getPages(token);
          const pagesData = pagesRes.data || pagesRes || [];
          setPagesList(pagesData);
          setDataLoaded(prev => ({ ...prev, pages: true }));
        } catch (error) {
          console.error("Failed to load pages:", error);
          toast.error('Failed to load pages data', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } finally {
          setLoadingPages(false);
        }
      }

      // Load Languages
      if (!dataLoaded.languages) {
        setLoadingLanguages(true);
        try {
          const langsRes = await getLanguages();
          setLanguagesList(langsRes.data || langsRes || []);
          setDataLoaded(prev => ({ ...prev, languages: true }));
        } catch (error) {
          console.error("Failed to load languages:", error);
          toast.error('Failed to load languages data', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } finally {
          setLoadingLanguages(false);
        }
      }
    };

    loadAllData();
  }, [isOpen]);

  // Load edit data
  useEffect(() => {
    if (isOpen && campaignToEdit) {
      reset({
        campaign: {
          Name: campaignToEdit.campaign.Name || '',
          Description: campaignToEdit.campaign.Description || '',
          StartDate: campaignToEdit.campaign.StartDate || '',
          EndDate: campaignToEdit.campaign.EndDate || '',
          Priority: campaignToEdit.campaign.Priority || 0,
          IsActive: campaignToEdit.campaign.IsActive || false,
        },
      });

      setSelectedCropIds(campaignToEdit.crops?.map(c => c.CropID) || []);
      setSelectedCountryIds(campaignToEdit.countries?.map(c => c.CountryID) || []);
      setSelectedPageIds(campaignToEdit.pages?.map(p => p.PageID) || []);
      
      // Set banners with proper structure
      const existingBanners = campaignToEdit.banners || [];
      setBanners(existingBanners.map(banner => ({
        ...banner,
        LanguageID: banner.LanguageID || '',
        DisplayOrder: banner.DisplayOrder || 0,
        FileName: banner.FileName || '',
        isExisting: true,
        BannerID: banner.ID || banner.BannerID || null
      })));
      
      setBannerFiles({});
      setBannersToRemove([]);
    } else if (isOpen && !campaignToEdit) {
      reset({
        campaign: {
          Name: '',
          Description: '',
          StartDate: '',
          EndDate: '',
          Priority: 0,
          IsActive: true,
        },
      });
      setSelectedCropIds([]);
      setSelectedCountryIds([]);
      setSelectedPageIds([]);
      setBanners([]);
      setBannerFiles({});
      setBannersToRemove([]);
    }
  }, [isOpen, campaignToEdit, reset]);

  // Clear crop selections and invalid banner languages when countries change
  useEffect(() => {
    // Clear crops that are no longer allowed
    if (selectedCountryIds.length > 0 && cropsList.length > 0) {
      const allowedCropIds = cropsList
        .filter(crop => {
          const allowedCountries = parseAllowedCountries(crop.AllowedCountries);
          return allowedCountries.some(countryId => selectedCountryIds.includes(countryId));
        })
        .map(crop => crop.ID);

      setSelectedCropIds(prev =>
        prev.filter(id => allowedCropIds.includes(id))
      );
    }

    // Clear banner languages that are no longer valid for selected countries
    if (selectedCountryIds.length > 0 && languagesList.length > 0) {
      const validLanguageIds = getFilteredLanguages().map(lang => lang.ID);

      setBanners(prev =>
        prev.map(banner => {
          // Only update if the banner's language is invalid
          if (
            banner.LanguageID &&
            !isEnglishLanguage(banner.LanguageID) &&
            !validLanguageIds.includes(banner.LanguageID)
          ) {
            return { ...banner, LanguageID: '' };
          }
          return banner;
        })
      );
    }
  }, [selectedCountryIds, cropsList, languagesList]);

  // Checklist handlers
  const handleSelectAll = (items, selectedIds, setSelectedIds) => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map(item => item.ID));
    }
  };

  const handleToggle = (id, selectedIds, setSelectedIds) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(itemId => itemId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Filter handlers
  const getFilteredItems = (items, search, searchFields) => {
    if (!search) return items;
    return items.filter(item =>
      searchFields.some(field =>
        item[field]?.toLowerCase().includes(search.toLowerCase())
      )
    );
  };

  // Banner handlers
  const addBanner = () => {
    setBanners([...banners, {
      LanguageID: ENGLISH_LANGUAGE_ID,
      DisplayOrder: banners.length + 1,
      FileName: '',
      isExisting: false,
      BannerID: null
    }]);
  };

  const removeBanner = (index) => {
    const bannerToRemove = banners[index];
    
    if (bannerToRemove.isExisting && bannerToRemove.BannerID) {
      setBannersToRemove(prev => [...prev, bannerToRemove.BannerID]);
    }
    
    setBanners(banners.filter((_, i) => i !== index));
    
    const newBannerFiles = {};
    Object.keys(bannerFiles).forEach(key => {
      if (parseInt(key) > index) {
        newBannerFiles[parseInt(key) - 1] = bannerFiles[key];
      } else if (parseInt(key) < index) {
        newBannerFiles[key] = bannerFiles[key];
      }
    });
    setBannerFiles(newBannerFiles);
    
    toast.info('Banner removed', {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const updateBanner = (index, field, value) => {
    const updatedBanners = [...banners];
    updatedBanners[index][field] = value;
    setBanners(updatedBanners);
  };

  const handleBannerFileUpload = (file, index) => {
    setBannerFiles(prev => ({ ...prev, [index]: file }));
    updateBanner(index, 'FileName', file.name);
    updateBanner(index, 'isModified', true);
    
    toast.success('Banner image added successfully', {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const onFormSubmit = async (data) => {
    try {
      setSaveLoading(true);

      const token = getAuthToken();
      if (!token) {
        setSaveLoading(false);
        return;
      }

      // Validate required fields
      if (selectedCropIds.length === 0) {
        toast.error('Please select at least one crop', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setSaveLoading(false);
        setActiveTab('basic');
        return;
      }

      if (banners.length === 0) {
        toast.error('Please add at least one banner', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setSaveLoading(false);
        setActiveTab('banners');
        return;
      }

      // For new banners, validate they have language and filename
      const invalidNewBanner = banners.find(b => !b.isExisting && (!b.LanguageID || !b.FileName));
      if (invalidNewBanner) {
        toast.error('Please fill in all new banner fields and upload images', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setSaveLoading(false);
        setActiveTab('banners');
        return;
      }

      // For existing banners, just ensure they have language selected
      const invalidExistingBanner = banners.find(b => b.isExisting && !b.LanguageID);
      if (invalidExistingBanner) {
        toast.error('Please select a language for all existing banners', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setSaveLoading(false);
        setActiveTab('banners');
        return;
      }

      const selectedPages = pagesList
        .filter(page => selectedPageIds.includes(page.ID))
        .map(page => ({
          PageID: page.ID,
          PageName: page.PageName
        }));

      // Prepare banner data for payload
      const bannerPayload = banners.map(b => ({
        LanguageID: b.LanguageID,
        DisplayOrder: parseInt(b.DisplayOrder) || 0,
        FileName: b.FileName || '',
        ...(b.isExisting && b.BannerID ? { BannerID: b.BannerID } : {}),
        isExisting: b.isExisting || false
      }));

      const payload = {
        campaign: {
          Name: data.campaign.Name,
          Description: data.campaign.Description,
          StartDate: data.campaign.StartDate,
          EndDate: data.campaign.EndDate,
          Priority: parseInt(data.campaign.Priority),
          IsActive: data.campaign.IsActive,
        },
        banners: bannerPayload,
        crops: selectedCropIds.map(id => ({ CropID: id })),
        pages: selectedPages,
        countries: selectedCountryIds.map(id => ({ CountryID: id })),
        ...(bannersToRemove.length > 0 ? { removeBannerIds: bannersToRemove } : {})
      };

      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));

      // Only append banner images that have been modified/added
      Object.keys(bannerFiles).forEach(index => {
        const banner = banners[parseInt(index)];
        if (!banner.isExisting || banner.isModified) {
          formData.append('bannerImages', bannerFiles[index]);
        }
      });

      let response;
      if (campaignToEdit) {
        response = await updateCampaign(campaignToEdit.campaign.ID, formData, token);
      } else {
        response = await addCampaign(formData, token);
      }

      if (response.status === "success") {
        toast.success(
          campaignToEdit ? 'Campaign updated successfully!' : 'Campaign added successfully!', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );

        if (onSuccess) {
          onSuccess();
        }

        setTimeout(() => {
          onClose();
          reset();
          setSaveLoading(false);
        }, 1500);
      } else {
        toast.error(response.message || 'Failed to save campaign', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setSaveLoading(false);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(
        error.response?.data?.message || error.message || 'An error occurred while saving.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
      setSaveLoading(false);
    }
  };

  const TabNav = () => (
    <div className="d-flex gap-2 mb-4 border-bottom pb-3 flex-wrap">
      <button
        type="button"
        className={`main-tab ${activeTab === 'basic' ? 'active' : ''}`}
        onClick={() => setActiveTab('basic')}
      >
        Basic Info
      </button>
      <button
        type="button"
        className={`main-tab ${activeTab === 'banners' ? 'active' : ''}`}
        onClick={() => setActiveTab('banners')}
      >
        Banners ({banners.length})
        {loadingLanguages && <span className="ms-1 spinner-border spinner-border-sm"></span>}
      </button>
    </div>
  );

  const renderBasicInfo = () => {
    const isLoading = loadingCrops || loadingCountries || loadingPages;

    if (isLoading && !dataLoaded.crops && !dataLoaded.countries && !dataLoaded.pages) {
      return (
        <div className="tab-loading">
          <div className="spinner-border text-primary"></div>
          <span className="ms-2">Loading data...</span>
        </div>
      );
    }

    const filteredCountries = getFilteredItems(countriesList, countrySearch, ['CountryName']);
    const filteredPages = getFilteredItems(pagesList, pageSearch, ['PageName']);
    const filteredCrops = getFilteredCrops();

    return (
      <>
        {/* Campaign Details */}
        <div className="form-grid-container">
          <div>
            <label className="form-label">Campaign Name <span className="required-star">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.campaign?.Name ? 'border-danger' : ''}`}
              {...register('campaign.Name', { required: 'Campaign name is required' })}
            />
            {errors.campaign?.Name && <span className="error-message">{errors.campaign.Name.message}</span>}
          </div>

          <div>
            <label className="form-label">Priority <span className="required-star">*</span></label>
            <input
              type="number"
              className={`form-control ${errors.campaign?.Priority ? 'border-danger' : ''}`}
              {...register('campaign.Priority', { required: 'Priority is required', min: 0 })}
            />
            {errors.campaign?.Priority && <span className="error-message">{errors.campaign.Priority.message}</span>}
          </div>

          <div>
            <label className="form-label">Start Date <span className="required-star">*</span></label>
            <input
              type="date"
              className={`form-control ${errors.campaign?.StartDate ? 'border-danger' : ''}`}
              {...register('campaign.StartDate', { required: 'Start date is required' })}
            />
            {errors.campaign?.StartDate && <span className="error-message">{errors.campaign.StartDate.message}</span>}
          </div>

          <div>
            <label className="form-label">End Date <span className="required-star">*</span></label>
            <input
              type="date"
              className={`form-control ${errors.campaign?.EndDate ? 'border-danger' : ''}`}
              {...register('campaign.EndDate', { required: 'End date is required' })}
            />
            {errors.campaign?.EndDate && <span className="error-message">{errors.campaign.EndDate.message}</span>}
          </div>
        </div>

        <div>
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows="3"
            {...register('campaign.Description')}
          ></textarea>
        </div>

        <hr className="section-divider" />

        {/* Countries */}
        <h6 className="section-title">Countries</h6>
        <div className="checklist-container">
          <div className="checklist-title">
            <span>Select Countries</span>
            <button
              type="button"
              className="select-all-btn"
              onClick={() => handleSelectAll(countriesList, selectedCountryIds, setSelectedCountryIds)}
            >
              {selectedCountryIds.length === countriesList.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <input
            type="text"
            placeholder="Search countries..."
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            className="form-control"
            style={{ marginBottom: '10px' }}
          />

          <div className="checklist-grid">
            {filteredCountries.map((country) => (
              <div key={country.ID} className="checklist-item">
                <div
                  className={`checklist-checkbox ${selectedCountryIds.includes(country.ID) ? 'checked' : ''}`}
                  onClick={() => handleToggle(country.ID, selectedCountryIds, setSelectedCountryIds)}
                />
                <label
                  className="checklist-label"
                  onClick={() => handleToggle(country.ID, selectedCountryIds, setSelectedCountryIds)}
                >
                  {country.CountryName}
                </label>
              </div>
            ))}
          </div>

          <div className="selected-count">
            {selectedCountryIds.length} of {countriesList.length} countries selected
          </div>
        </div>

        {/* Crops - Filtered by selected countries */}
        <h6 className="section-title">Crops <span className="required-star">*</span></h6>
        <div className="checklist-container">
          <div className="checklist-title">
            <span>
              Select Crops <span className="required-star">*</span>
              {selectedCountryIds.length > 0 && (
                <span className="country-hint" style={{ marginLeft: '8px' }}>
                  (Filtered by selected countries)
                </span>
              )}
            </span>
            {selectedCountryIds.length > 0 && filteredCrops.length > 0 && (
              <button
                type="button"
                className="select-all-btn"
                onClick={() => handleSelectAll(filteredCrops, selectedCropIds, setSelectedCropIds)}
              >
                {selectedCropIds.length === filteredCrops.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          <input
            type="text"
            placeholder="Search crops..."
            value={cropSearch}
            onChange={(e) => setCropSearch(e.target.value)}
            className="form-control"
            style={{ marginBottom: '10px' }}
          />

          {selectedCountryIds.length === 0 && (
            <div className="alert alert-info d-flex align-items-center" style={{ fontSize: '0.875rem', padding: '8px 12px', marginBottom: '10px' }}>
              <Icon icon="bi:info-circle" /> Please select at least one country to see available crops.
            </div>
          )}

          {selectedCountryIds.length > 0 && filteredCrops.length === 0 && (
            <div className="no-crops-message">
              <Icon icon="bi:info-circle" style={{ marginRight: '8px' }} />
              No crops available for the selected countries.
            </div>
          )}

          <div className="checklist-grid">
            {filteredCrops.map((crop) => (
              <div key={crop.ID} className="checklist-item">
                <div
                  className={`checklist-checkbox ${selectedCropIds.includes(crop.ID) ? 'checked' : ''}`}
                  onClick={() => handleToggle(crop.ID, selectedCropIds, setSelectedCropIds)}
                />
                <label
                  className="checklist-label"
                  onClick={() => handleToggle(crop.ID, selectedCropIds, setSelectedCropIds)}
                >
                  {crop.CropName || crop.CropShortName || `Crop ${crop.ID}`}
                  {crop.CropShortName && crop.CropName && (
                    <span style={{ fontSize: '0.7rem', color: '#6b7280', marginLeft: '4px' }}>
                      ({crop.CropShortName})
                    </span>
                  )}
                </label>
              </div>
            ))}
          </div>

          <div className="selected-count">
            {selectedCropIds.length} of {filteredCrops.length} crops selected
            {selectedCountryIds.length > 0 && filteredCrops.length > 0 && (
              <span style={{ marginLeft: '8px', fontSize: '0.7rem', color: '#6b7280' }}>
                (filtered from {cropsList.length} total)
              </span>
            )}
          </div>

          {selectedCropIds.length === 0 && selectedCountryIds.length > 0 && (
            <span className="error-message">Please select at least one crop</span>
          )}
        </div>

        {/* Pages */}
        <h6 className="section-title">Pages</h6>
        <div className="checklist-container">
          <div className="checklist-title">
            <span>Select Pages</span>
            <button
              type="button"
              className="select-all-btn"
              onClick={() => handleSelectAll(pagesList, selectedPageIds, setSelectedPageIds)}
            >
              {selectedPageIds.length === pagesList.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="checklist-grid">
            {filteredPages.map((page) => (
              <div key={page.ID} className="checklist-item">
                <div
                  className={`checklist-checkbox ${selectedPageIds.includes(page.ID) ? 'checked' : ''}`}
                  onClick={() => handleToggle(page.ID, selectedPageIds, setSelectedPageIds)}
                />
                <label
                  className="checklist-label"
                  onClick={() => handleToggle(page.ID, selectedPageIds, setSelectedPageIds)}
                >
                  {page.PageName}
                </label>
              </div>
            ))}
          </div>

          <div className="selected-count">
            {selectedPageIds.length} of {pagesList.length} pages selected
          </div>
        </div>

        {/* Status - Moved to bottom */}
        <div className="status-section">
          <div className="form-check style-check d-flex align-items-center gap-8">
            <input
              className="form-check-input radius-4 border border-neutral-400"
              type="checkbox"
              {...register('campaign.IsActive')}
              checked={watch('campaign.IsActive')}
            />
            <label className="form-label fw-medium text-primary-light text-sm mb-0">
              Active
            </label>
          </div>
        </div>
      </>
    );
  };

  const renderBanners = () => {
    if (loadingLanguages && !dataLoaded.languages) {
      return (
        <div className="tab-loading">
          <div className="spinner-border text-primary"></div>
          <span className="ms-2">Loading languages...</span>
        </div>
      );
    }

    const filteredLanguages = getFilteredLanguages();

    return (
      <div className="banner-upload-section">
        <h6 className="section-title">Banner Management</h6>

        {selectedCountryIds.length > 0 && filteredLanguages.length === 0 && (
          <div className="no-languages-message">
            <Icon icon="bi:info-circle" style={{ marginRight: '8px' }} />
            No languages available for the selected countries.
          </div>
        )}

        {banners.map((banner, index) => (
          <div key={index} className="banner-item">
            <button
              type="button"
              className="remove-banner-btn"
              onClick={() => removeBanner(index)}
            >
              <Icon icon="lucide:x" />
            </button>

            <div className="banner-language-select">
              <select
                className="form-control"
                value={banner.LanguageID || ''}
                onChange={(e) => updateBanner(index, 'LanguageID', e.target.value)}
                disabled={filteredLanguages.length === 0}
              >
                <option value="">Select Language</option>
                {filteredLanguages.map(lang => (
                  <option key={lang.ID} value={lang.ID}>
                    {lang.LanguageName} {lang.LanguageCode && `(${lang.LanguageCode})`}
                  </option>
                ))}
              </select>
            </div>

            <div className="banner-display-order">
              <input
                type="number"
                className="form-control"
                value={banner.DisplayOrder || 0}
                onChange={(e) => updateBanner(index, 'DisplayOrder', parseInt(e.target.value) || 0)}
                placeholder="Order"
              />
            </div>

            {banner.isExisting && !banner.isModified ? (
              // Show existing banner file info
              <div className="banner-existing-file">
                <Icon icon="bi:check-circle-fill" style={{ color: '#28a745' }} />
                <span>{banner.FileName || 'Existing banner'}</span>
                <span style={{ fontSize: '0.7rem', color: '#6b7280', marginLeft: '4px' }}>
                  (Click "Change Image" to replace)
                </span>
              </div>
            ) : (
              // Show upload for new or modified banners
              <div className="file-input-wrapper">
                <label htmlFor={`banner-${index}`} className="file-input-label">
                  <Icon icon="bi:image" />
                  <span>{banner.FileName ? 'Change Image' : 'Upload Image'}</span>
                </label>
                <input
                  id={`banner-${index}`}
                  type="file"
                  accept="image/*"
                  className="file-input"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleBannerFileUpload(file, index);
                    }
                  }}
                />
              </div>
            )}

            {banner.FileName && !banner.isExisting && (
              <div className="upload-progress">
                <Icon icon="bi:check-circle-fill" style={{ color: '#28a745' }} />
                <span className="ms-1">{banner.FileName}</span>
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          className="add-banner-btn"
          onClick={addBanner}
          disabled={filteredLanguages.length === 0}
        >
          <Icon icon="lucide:plus" /> Add Banner
        </button>

        {selectedCountryIds.length > 0 && filteredLanguages.length > 0 && banners.length === 0 && (
          <div className="mt-3 text-muted" style={{ fontSize: '0.875rem' }}>
            No banners added yet. Click "Add Banner" to upload.
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{customStyles}</style>
      <Drawer open={isOpen} onClose={onClose} direction="right" size={750} style={{ zIndex: 1050 }}>
        <div className="drawer-header">
          <h6 className="drawer-title">
            {campaignToEdit ? 'Edit Campaign' : 'Add New Campaign'}
          </h6>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '1rem',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            <Icon icon="lucide:x" />
          </button>
        </div>

        <div className="card-body">
          {saveLoading && (
            <div className="loading-overlay">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onFormSubmit)}>
            <TabNav />

            {activeTab === 'basic' ? renderBasicInfo() : renderBanners()}

            <div className="d-flex align-items-center justify-content-center gap-3 mt-24" style={{ marginTop: '2rem' }}>
              <button type="button" className="btn btn-secondary px-48 py-10 radius-8" onClick={onClose} disabled={saveLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-yellow px-48 py-10 radius-8" disabled={saveLoading}>
                {saveLoading ? 'Saving...' : (campaignToEdit ? 'Update Campaign' : 'Save Campaign')}
              </button>
            </div>
          </form>
        </div>
      </Drawer>
    </>
  );
};

export default CampaignDrawer;
